import { useEffect, useState } from 'react';
import Alert, { AlertVariant, AlertSize } from '../../atoms/alert/Alert';
import Button, { ButtonSize, ButtonVariant } from '../../atoms/buttons/button/Button';
import { type QuestionValue, type Survey, type SurveyStatsData } from './types';
import { SurveyList } from './SurveyList';
import { SurveyStatsView } from './SurveyStatsView';
import { SurveyForm } from './SurveyForm';

type View = 'list' | 'stats' | 'form';

export function SondageManager() {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [view, setView] = useState<View>('list');
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [stats, setStats] = useState<SurveyStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(false);
  const [editQuestions, setEditQuestions] = useState<QuestionValue[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  function loadSurveys() {
    setLoading(true);
    setError(false);

    fetch('/sondage/listThemes')
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json() as Promise<Survey[]>;
      })
      .then((data) => {
        setSurveys(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }

  useEffect(() => {
    loadSurveys();
  }, []);

  function openStats(survey: Survey) {
    setSelectedSurvey(survey);
    setView('stats');
    setStats(null);
    setStatsError(false);
    setStatsLoading(true);

    fetch(`/sondage/themeStats?id_theme=${survey.id}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json() as Promise<SurveyStatsData>;
      })
      .then((data) => {
        setStats(data);
        setStatsLoading(false);
      })
      .catch(() => {
        setStatsError(true);
        setStatsLoading(false);
      });
  }

  function backToList() {
    setView('list');
    setSelectedSurvey(null);
    setStats(null);
  }

  function openCreate() {
    setSelectedSurvey(null);
    setEditQuestions([]);
    setView('form');
  }

  function openEdit(survey: Survey) {
    setFormLoading(true);

    fetch(`/sondage/themeStats?id_theme=${survey.id}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json() as Promise<SurveyStatsData>;
      })
      .then((data) => {
        setSelectedSurvey(survey);
        setEditQuestions(data.questions.map((q) => ({ text: q.question, type: q.type })));
        setFormLoading(false);
        setView('form');
      })
      .catch(() => {
        setSelectedSurvey(survey);
        setEditQuestions([]);
        setFormLoading(false);
        setView('form');
      });
  }

  function handleFormSuccess() {
    setView('list');
    setSelectedSurvey(null);
    setEditQuestions([]);
    loadSurveys();
  }

  function handleDelete(surveyId: number) {
    setActionError(null);
    fetch('/sondage/deleteSurvey', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: surveyId }),
    })
      .then((r) => {
        if (!r.ok) return r.text().then((msg) => { throw new Error(msg); });
        loadSurveys();
      })
      .catch((e: Error) => {
        setActionError(e.message || 'Impossible de supprimer le sondage.');
      });
  }

  function handleDuplicate(survey: Survey) {
    setActionError(null);
    fetch('/sondage/duplicateSurvey', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: survey.id }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('Impossible de dupliquer le sondage.');
        return r.json() as Promise<{ id: number; theme: string; description: string; date_start: string; date_end: string }>;
      })
      .then((data) => {
        const newSurvey: Survey = {
          id: data.id,
          theme: data.theme,
          description: data.description,
          date_start: data.date_start,
          date_end: data.date_end,
          question_count: 0,
          response_count: 0,
          status: 'upcoming',
        };
        openEdit(newSurvey);
      })
      .catch((e: Error) => {
        setActionError(e.message);
      });
  }

  return (
    <div className="p-6 mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-neutral-800">Gestion des sondages</h1>
        <Button variant={ButtonVariant.BRAND} size={ButtonSize.LG} onClick={openCreate}>
          Nouveau sondage
        </Button>
      </div>

      {loading && (
        <p className="py-12 text-center text-xl text-neutral-400">Chargement...</p>
      )}

      {error && (
        <Alert variant={AlertVariant.DESTRUCTIVE} size={AlertSize.SMALL}>
          Impossible de charger les sondages.
        </Alert>
      )}

      {actionError && (
        <Alert variant={AlertVariant.DESTRUCTIVE} size={AlertSize.SMALL}>
          {actionError}
        </Alert>
      )}

      {formLoading && (
        <p className="py-12 text-center text-xl text-neutral-400">Chargement...</p>
      )}

      {!loading && !error && !formLoading && view === 'list' && (
        <SurveyList
          surveys={surveys}
          onViewStats={openStats}
          onEdit={openEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      )}

      {!loading && !error && !formLoading && view === 'stats' && selectedSurvey && (
        <SurveyStatsView
          survey={selectedSurvey}
          stats={stats}
          loading={statsLoading}
          error={statsError}
          onBack={backToList}
        />
      )}

      {!formLoading && view === 'form' && (
        <SurveyForm
          survey={selectedSurvey ?? undefined}
          initialQuestions={editQuestions.length > 0 ? editQuestions : undefined}
          onSuccess={handleFormSuccess}
          onCancel={backToList}
        />
      )}
    </div>
  );
}
